import { logEvent } from './telemetry';

const HOUR_MS = 60 * 60 * 1000;
export const TRIAL_INTENT_KEY = 'eh.trialIntent';
export const TRIAL_INTENT_TTL_MS = HOUR_MS;
// Coarse age of an expired intent for telemetry — enough to tell "came back the next day"
// from "sign-up took a little over an hour", with no timestamp that could identify a visit.
const AGE_BUCKETS: ReadonlyArray<readonly [belowMs: number, label: string]> = [
  [2 * HOUR_MS, '1h-2h'],
  [6 * HOUR_MS, '2h-6h'],
  [24 * HOUR_MS, '6h-24h'],
];

const INTENT_PARAM = 'intent';
// Carried only across the org-host bounce (withTrialIntent), where sessionStorage does not follow,
// so the TTL does not restart and an attempted sign-up is not re-armed on the other host.
const CAPTURED_AT_PARAM = 'trial_captured_at';
const ATTEMPTED_AT_PARAM = 'trial_attempted_at';
const CARRIED_PARAMS = [CAPTURED_AT_PARAM, ATTEMPTED_AT_PARAM] as const;
const TIMESTAMP = /^\d{1,15}$/;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const UTM_VALUE = /^[A-Za-z0-9._\-+ ]{1,100}$/;
const MAX_EMAIL_LENGTH = 254;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+$/;

export type TrialUtmKey = (typeof UTM_KEYS)[number];
export type TrialUtm = Partial<Record<TrialUtmKey, string>>;
export type TrialIntent = {
  product: 'guardian';
  utm: TrialUtm;
  capturedAt: number;
  attemptedAt?: number;
};

// `guardian-essentials-trial` is the id the live website form used first; it is still accepted, but
// the Hub only ever writes `guardian-trial`.
const PRODUCT_BY_INTENT = new Map<string, TrialIntent['product']>([
  ['guardian-trial', 'guardian'],
  ['guardian-essentials-trial', 'guardian'],
]);
const INTENT_BY_PRODUCT: Record<TrialIntent['product'], string> = { guardian: 'guardian-trial' };

let loginHint: string | null = null;

function pickUtm(read: (key: TrialUtmKey) => unknown): TrialUtm {
  const utm: TrialUtm = {};
  for (const key of UTM_KEYS) {
    const value = read(key);
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (UTM_VALUE.test(trimmed)) utm[key] = trimmed;
  }
  return utm;
}

function parseIntent(raw: unknown): TrialIntent | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { product, utm, capturedAt, attemptedAt } = raw as Record<string, unknown>;
  if (product !== 'guardian' || typeof capturedAt !== 'number' || !Number.isFinite(capturedAt)) return null;
  const source = typeof utm === 'object' && utm !== null ? (utm as Record<string, unknown>) : {};
  const intent: TrialIntent = { product, utm: pickUtm((key) => source[key]), capturedAt };
  if (typeof attemptedAt === 'number') intent.attemptedAt = attemptedAt;
  return intent;
}

function isExpired(intent: TrialIntent, now = Date.now()): boolean {
  return now - intent.capturedAt > TRIAL_INTENT_TTL_MS;
}

function ageBucket(ageMs: number): string {
  return AGE_BUCKETS.find(([below]) => ageMs < below)?.[1] ?? '>24h';
}

export function storeTrialIntent(intent: TrialIntent): void {
  try {
    sessionStorage.setItem(TRIAL_INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* storage unavailable: the intent just won't survive the sign-up redirect */
  }
}

// The stored intent, expired or not; a malformed entry is dropped.
function readStoredIntent(): TrialIntent | null {
  try {
    const raw = sessionStorage.getItem(TRIAL_INTENT_KEY);
    if (!raw) return null;
    const intent = parseIntent(JSON.parse(raw));
    if (!intent) sessionStorage.removeItem(TRIAL_INTENT_KEY);
    return intent;
  } catch {
    return null;
  }
}

// Whether a live intent is stored, without reporting or dropping an expired one: for surfaces
// that must decide before the release flag is known, when the expiry is not yet worth a metric.
export function peekTrialIntent(): TrialIntent | null {
  const intent = readStoredIntent();
  return intent && !isExpired(intent) ? intent : null;
}

// Only for callers acting on the intent, i.e. with `release-hub-trial-auto-start` on.
export function readTrialIntent(): TrialIntent | null {
  const intent = readStoredIntent();
  if (!intent || !isExpired(intent)) return intent;
  try {
    // Removed first, so the expiry is reported once however many readers follow.
    sessionStorage.removeItem(TRIAL_INTENT_KEY);
  } catch {
    /* ignore */
  }
  logEvent('cloudhouse.enterprise.trial.intent_expired', 'Stored trial intent expired before it was used',
    { product: intent.product, age_bucket: ageBucket(Date.now() - intent.capturedAt) }, 'INFO');
  return null;
}

export function clearTrialIntent(): void {
  loginHint = null;
  try {
    sessionStorage.removeItem(TRIAL_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

export function getLoginHint(): string | null {
  return loginHint;
}

// The only proof hub home accepts for starting a trial unasked: the terms page in *this* tab
// recorded the acceptance. An org-wide `acceptedAt` would also cover a colleague accepting in
// another session, and a sign-in round trip proves nothing about what the admin agreed to.
const termsAcceptedKey = (orgId: string) => `eh.termsAccepted:${orgId}`;

export function markTermsAcceptedInSession(orgId: string): void {
  try {
    sessionStorage.setItem(termsAcceptedKey(orgId), '1');
  } catch {
    /* storage unavailable: hub home asks the admin to confirm instead */
  }
}

// Cleared on read, so the proof is spent by the first decision it informs.
export function consumeTermsAcceptedInSession(orgId: string): boolean {
  try {
    const key = termsAcceptedKey(orgId);
    const marked = sessionStorage.getItem(key) === '1';
    sessionStorage.removeItem(key);
    return marked;
  } catch {
    return false;
  }
}

function updateStoredIntent(change: Partial<TrialIntent>): void {
  const intent = readTrialIntent();
  if (intent) storeTrialIntent({ ...intent, ...change });
}

export function markSignupAttempted(): void {
  updateStoredIntent({ attemptedAt: Date.now() });
}

function timestampParam(params: URLSearchParams, key: string, now: number): number | undefined {
  const raw = params.get(key);
  if (raw === null || !TIMESTAMP.test(raw)) return undefined;
  const value = Number(raw);
  // A future timestamp would stretch the TTL indefinitely; treat it as absent.
  return value <= now ? value : undefined;
}

// Idempotent: a reload or Back with `intent` still in the URL keeps the stored intent, so its TTL
// does not restart and its attemptedAt still stops a second automatic sign-up.
function storedOrNewIntent(product: TrialIntent['product'], params: URLSearchParams): TrialIntent {
  const stored = peekTrialIntent();
  if (stored?.product === product) return stored;
  const now = Date.now();
  const intent: TrialIntent = {
    product,
    utm: pickUtm((key) => params.get(key)),
    capturedAt: timestampParam(params, CAPTURED_AT_PARAM, now) ?? now,
  };
  const attemptedAt = timestampParam(params, ATTEMPTED_AT_PARAM, now);
  if (attemptedAt !== undefined) intent.attemptedAt = attemptedAt;
  storeTrialIntent(intent);
  return intent;
}

function validLoginHint(email: string | null): string | null {
  const trimmed = email?.trim() ?? '';
  return trimmed.length <= MAX_EMAIL_LENGTH && EMAIL_SHAPE.test(trimmed) ? trimmed : null;
}

// The address without the #email= fragment and the org-host bounce params, or null if unchanged.
// An arrival from the bounce also loses `intent` and `utm_*`: the intent is already captured, and a
// reload must neither re-arm it nor count the visit to the campaign a second time.
function cleanedAddress(loc: Pick<Location, 'pathname' | 'search' | 'hash'>, params: URLSearchParams, hasEmail: boolean): string | null {
  const carried = CARRIED_PARAMS.some((key) => params.has(key));
  if (!hasEmail && !carried) return null;
  let search = loc.search;
  if (carried) {
    for (const key of [...CARRIED_PARAMS, INTENT_PARAM, ...UTM_KEYS]) params.delete(key);
    const query = params.toString();
    search = query ? `?${query}` : '';
  }
  return loc.pathname + search + (hasEmail ? '' : loc.hash);
}

// Must run before any analytics initialises: it removes the #email= fragment from the address bar.
// On a first arrival `intent` and `utm_*` stay in the URL, so attribution (GA4, Clarity) reads them
// as before; only the org-host bounce's copy of them is stripped.
export function captureTrialIntent(
  loc: Pick<Location, 'pathname' | 'search' | 'hash'>,
  hist: Pick<History, 'state' | 'replaceState'> = globalThis.history,
): TrialIntent | null {
  const email = new URLSearchParams(loc.hash.replace(/^#/, '')).get('email');
  const params = new URLSearchParams(loc.search);
  const product = PRODUCT_BY_INTENT.get(params.get(INTENT_PARAM) ?? '');

  let intent: TrialIntent | null = null;
  if (product) {
    intent = storedOrNewIntent(product, params);
    const hint = validLoginHint(email);
    if (hint) loginHint = hint;
  }

  const address = cleanedAddress(loc, params, email !== null);
  if (address !== null) hist.replaceState(hist.state, '', address);
  return intent;
}

// An `intent` the Hub does not recognise is dropped; reported (without its value) so a website
// form sending a mistyped intent shows up. Separate from capture because App Insights starts after it.
export function reportUnrecognisedTrialIntent(loc: Pick<Location, 'search'>): void {
  const value = new URLSearchParams(loc.search).get(INTENT_PARAM);
  if (value !== null && !PRODUCT_BY_INTENT.has(value)) {
    logEvent('cloudhouse.enterprise.trial.intent_rejected', 'Unrecognised trial intent in the URL', {}, 'WARN');
  }
}

export function trialSourceFields(intent: TrialIntent): Record<string, string> {
  const fields: Record<string, string> = { product: intent.product, intent: INTENT_BY_PRODUCT[intent.product] };
  for (const key of UTM_KEYS) {
    const value = intent.utm[key];
    if (value) fields[key] = value;
  }
  return fields;
}

export function withTrialIntent(url: string, intent: TrialIntent | null): string {
  if (!intent) return url;
  const target = new URL(url);
  target.searchParams.set(INTENT_PARAM, INTENT_BY_PRODUCT[intent.product]);
  for (const key of UTM_KEYS) {
    const value = intent.utm[key];
    if (value) target.searchParams.set(key, value);
  }
  target.searchParams.set(CAPTURED_AT_PARAM, String(intent.capturedAt));
  if (intent.attemptedAt !== undefined) target.searchParams.set(ATTEMPTED_AT_PARAM, String(intent.attemptedAt));
  return target.toString();
}
